import { useLocation, useNavigate } from 'react-router-dom';

import { observer } from 'mobx-react-lite';

import { IconAddPlus } from '@components/icons/add-plus';
import { IconArchive } from '@components/icons/archive';
import { IconCopy } from '@components/icons/copy';
import { IconDownload } from '@components/icons/download';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconMove } from '@components/icons/move';
import { IconShare } from '@components/icons/share';
import { IconVisibilityHidden } from '@components/icons/visibility-hidden';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { Menu } from '@components/menu';

import { useInvalidateJobListAfterReindex } from 'api/jobs/hooks';
import { useFavoriteActionItem } from 'components/navigation/favorites';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { visibleJobActions } from 'helpers/job-actions';
import { useManageJobAccess } from 'hooks/use-manage-job-access';
import { useStore } from 'models/Provider';
import {
  exportListScope,
  useExportApplicantsSidebar,
} from 'pages/Job/sidebars/export-applicants-sidebar';

const FavoriteMenuItem = ({ jobId }) => {
  const getFavoriteAction = useFavoriteActionItem('job');
  const favoriteItem = getFavoriteAction(jobId);

  if (!favoriteItem) {
    return null;
  }

  return (
    <Menu.Item
      onSelect={favoriteItem.onSelect}
      icon={favoriteItem.icon}
      label={favoriteItem.label}
    />
  );
};

export const JobOptions = observer(({ job, transparent = true }) => {
  const { t } = useStore();
  const { hasRolePermission } = usePermissions();
  const { showModal } = useModal();
  const hasManageAccess = useManageJobAccess(job.createdById?.id);
  const location = useLocation();
  const navigate = useNavigate();
  const showExportApplicantsSidebar = useExportApplicantsSidebar();
  const invalidateJobListAfterReindex = useInvalidateJobListAfterReindex();

  // These actions write to MST, but the jobs list reads react-query.
  function withListRefresh(action) {
    return async (...args) => {
      const result = await action(...args);
      invalidateJobListAfterReindex();
      return result;
    };
  }

  async function publish() {
    let success = true;
    if (!job.isPublished) {
      success = await job.publish();
    }

    if (!success) return;

    invalidateJobListAfterReindex();

    showModal('JOB_PUBLISHED', {
      _container: {
        closeOnOverlayClickOrEsc: false,
        autoWidth: true,
        minWidth: '500px',
      },
      job,
      t,
    });
  }

  function addStage() {
    job.addStage();
  }

  function shareModal(job) {
    showModal('SHARE_JOB_MODAL', {
      _container: {
        autoWidth: false,
        closeOnOverlayClickOrEsc: false,
      },
      job: job,
    });
  }

  function archive() {
    // Since ConfirmModal isn't typed, here we don't know `negative` isn't a required prop.
    // @ts-expect-error
    showModal('CONFIRM_MODAL', {
      _container: {
        minWidth: '200px',
        autoWidth: true,
      },
      data: job,
      confirm: withListRefresh(job.moveToArchive),
      buttonIcon: <IconArchive />,
      headerText: t('job.archive.modal.header'),
      mainText: t('job.archive.modal.text', job.title),
      confirmText: t('job.archive.modal.button'),
    });
  }

  if (!job) return null;

  const visible = visibleJobActions({ job, hasManageAccess, hasRolePermission });

  return (
    <Menu
      buttonProps={{
        iconLeft: <IconMoreVertical />,
        variant: transparent ? 'ghost' : 'secondary',
      }}
      placement="left-start"
    >
      {hasManageAccess && (
        <>
          <Menu.Item onSelect={addStage} icon={<IconAddPlus />} label={t('job.options.addstage')} />
          <Menu.Separator />
        </>
      )}
      {visible.has('edit') && (
        <Menu.Item
          icon={<IconEdit />}
          label={t('job.options.edit')}
          onSelect={() =>
            navigate(`/job/${job.id}/edit/description`, { state: { from: location.pathname } })
          }
        />
      )}

      {visible.has('duplicate') && (
        <Menu.Item
          onSelect={withListRefresh(job.duplicate)}
          icon={<IconCopy />}
          label={t('job.options.duplicate')}
        />
      )}
      {visible.has('export') && (
        <Menu.Item
          onSelect={() =>
            showExportApplicantsSidebar({ job: job, listScope: exportListScope(job) })
          }
          icon={<IconDownload />}
          label={t('job.options.export')}
        />
      )}

      <FavoriteMenuItem jobId={job.id} />

      {visible.has('share') && (
        <Menu.Item
          onSelect={() => shareModal(job)}
          icon={<IconShare />}
          label={t('job.options.share')}
        />
      )}
      {hasManageAccess && <Menu.Separator />}
      {visible.has('unpublish') && (
        <Menu.Item
          onSelect={withListRefresh(job.unPublish)}
          icon={<IconVisibilityHidden />}
          label={t('job.options.unpublish')}
        />
      )}
      {visible.has('publish') && (
        <Menu.Item
          onSelect={publish}
          icon={<IconVisibilityShown />}
          label={t('job.options.publish')}
        />
      )}
      {visible.has('archive') && (
        <Menu.Item onSelect={archive} icon={<IconArchive />} label={t('job.options.archive')} />
      )}
      {visible.has('drafts') && (
        <Menu.Item
          onSelect={withListRefresh(job.moveToDrafts)}
          icon={<IconMove />}
          label={t('job.options.drafts')}
        />
      )}
    </Menu>
  );
});
